import { CLASS_ORDER, classLabel, painLabel, platformLabel } from "@/lib/commentLabels";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  platforms: Record<string, number>;
  classes: Record<string, { count: number; label: string }>;
  months: Record<string, number>;
  pains: Record<string, { count: number; label: string }>;
  platform?: string;
  storyClass?: string;
  month?: string;
  pain?: string;
  hideReplies: boolean;
  window?: string;
  onPlatform: (v: string | undefined) => void;
  onClass: (v: string | undefined) => void;
  onMonth: (v: string | undefined) => void;
  onPain: (v: string | undefined) => void;
  onHideReplies: (v: boolean) => void;
  onWindow?: (v: string) => void;
};

function Chip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm",
        active ? "bg-brand/10 font-medium text-brand" : "text-slate-700 hover:bg-slate-50",
      )}
    >
      <span className="truncate">{label}</span>
      {count != null && <span className="tabular-nums text-xs text-slate-400">{count}</span>}
    </button>
  );
}

export default function CommentsFilterSidebar(props: Props) {
  const monthEntries = Object.entries(props.months).sort((a, b) => b[0].localeCompare(a[0]));
  const classEntries = CLASS_ORDER
    .filter((id) => props.classes[id])
    .map((id) => [id, props.classes[id]] as const);
  const extraClasses = Object.entries(props.classes).filter(([id]) => !CLASS_ORDER.includes(id));
  const painEntries = Object.entries(props.pains).sort((a, b) => b[1].count - a[1].count);

  return (
    <aside className="w-full shrink-0 space-y-5 rounded-xl border border-slate-200 bg-white p-4 lg:w-56">
      {props.onWindow && (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Okno
          </div>
          {[
            ["24h", "24 godziny"],
            ["7d", "7 dni"],
            ["30d", "30 dni"],
            ["all", "Całość"],
          ].map(([id, label]) => (
            <Chip
              key={id}
              active={props.window === id}
              label={label}
              onClick={() => props.onWindow?.(id)}
            />
          ))}
        </div>
      )}
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Platforma
        </div>
        <Chip
          active={!props.platform}
          label="Wszystkie"
          onClick={() => props.onPlatform(undefined)}
        />
        {["instagram", "facebook", "youtube", "tiktok"].map((id) => (
          <Chip
            key={id}
            active={props.platform === id}
            label={platformLabel(id)}
            count={props.platforms[id] || 0}
            onClick={() => props.onPlatform(props.platform === id ? undefined : id)}
          />
        ))}
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Klasa
        </div>
        {[...classEntries, ...extraClasses].map(([id, slot]) => (
          <Chip
            key={id}
            active={props.storyClass === id}
            label={slot.label || classLabel(id)}
            count={slot.count}
            onClick={() => props.onClass(props.storyClass === id ? undefined : id)}
          />
        ))}
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ból
        </div>
        {painEntries.slice(0, 12).map(([id, slot]) => (
          <Chip
            key={id}
            active={props.pain === id}
            label={painLabel(id, slot.label)}
            count={slot.count}
            onClick={() => props.onPain(props.pain === id ? undefined : id)}
          />
        ))}
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Miesiąc
        </div>
        {monthEntries.slice(0, 10).map(([id, count]) => (
          <Chip
            key={id}
            active={props.month === id}
            label={id}
            count={count}
            onClick={() => props.onMonth(props.month === id ? undefined : id)}
          />
        ))}
      </div>
      <Button
        size="sm"
        variant={props.hideReplies ? "default" : "outline"}
        className="w-full"
        onClick={() => props.onHideReplies(!props.hideReplies)}
      >
        {props.hideReplies ? "Tylko głos (bez replies)" : "Pokaż też replies"}
      </Button>
    </aside>
  );
}
